import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { 
    Container, Row, Col, Card, CardBody, Nav, 
    NavItem, NavLink, TabContent, TabPane,
    Table, Pagination, PaginationItem, PaginationLink, Input, UncontrolledCarousel, Badge
} from 'reactstrap';
import BreadCrumb from '../../Components/Common/BreadCrumb';
import moment from 'moment';
import { toast } from 'react-toastify';
import { Carousel } from 'bootstrap';
import UserDetailsModal from "../../Components/Common/UserDetailsModal";
import useUserDetails from '../../hooks/useUserDetails';

const UserDetails = () => {
    const { userId, tab } = useParams();
    const [activeTab, setActiveTab] = useState(tab || 'profile');
    const [userData, setUserData] = useState(null);
    const [likes, setLikes] = useState([]);
    const [referrals, setReferrals] = useState([]);
    const [subscriptions, setSubscriptions] = useState([]);
    const [datingStatus, setDatingStatus] = useState([]);
    const [searchText, setSearchText] = useState('');
    const [loading, setLoading] = useState(false);
    const [pagination, setPagination] = useState({
        currentPage: 1,
        limit: 10,
        totalCount: 0
    });

    // Create refs object to store multiple video refs
    const videoRefs = useRef({});

    // Add the hook
    const { 
        userDetailsModal, 
        setUserDetailsModal, 
        selectedUser, 
        loadingUser, 
        fetchUserDetails 
    } = useUserDetails();

    const fetchLikes = async (page = 1) => {
        try {
            setLoading(true);
            const offset = (page - 1) * pagination.limit;
            const response = await fetch(
                `${process.env.REACT_APP_API_URL}/profilesBasedOnUserResponse?userId=${userId}&limit=${pagination.limit}&skip=${offset}`,
                {
                    headers: {
                        'Authorization': JSON.parse(sessionStorage.getItem("authUser"))?.token || '',
                    }
                }
            );
            const data = await response.json();
            if (data.message === "Success") {
                setLikes(data.data.userList);
                setPagination(prev => ({
                    ...prev,
                    currentPage: page,
                    totalCount: data.data.totalCount
                }));
            }
        } catch (error) {
            toast.error('Failed to fetch likes');
        } finally {
            setLoading(false);
        }
    };

    const fetchReferrals = async (page = 1) => {
        try {
            setLoading(true);
            const offset = (page - 1) * pagination.limit;
            const response = await fetch(
                `${process.env.REACT_APP_API_URL}/referrals/list?userId=${userId}&limit=${pagination.limit}&offset=${offset}`,
                {
                    headers: {
                        'Authorization': JSON.parse(sessionStorage.getItem("authUser"))?.token || '',
                    }
                }
            );
            const data = await response.json();
            if (data.message === "Success") {
                setReferrals(data.data.referralData);
                setPagination(prev => ({
                    ...prev,
                    currentPage: page,
                    totalCount: data.data.totalCount
                }));
            }
        } catch (error) {
            toast.error('Failed to fetch referrals');
        } finally {
            setLoading(false);
        }
    };

    const fetchSubscriptions = async (page = 1) => {
        try {
            setLoading(true);
            const offset = (page - 1) * pagination.limit;
            const searchParam = searchText ? `&searchText=${searchText}` : '';
            const response = await fetch(
                `${process.env.REACT_APP_API_URL}/admin/subscription?trigger=SUBSCRIPTION&userId=${userId}&offset=${offset}&status=ACTIVE&limit=${pagination.limit}${searchParam}`,
                {
                    headers: {
                        'Authorization': JSON.parse(sessionStorage.getItem("authUser"))?.token || '',
                    }
                }
            );
            const data = await response.json();
            if (data.message === "Success") {
                setSubscriptions(data.data.data);
                setPagination(prev => ({
                    ...prev,
                    currentPage: page,
                    totalCount: data.data.totalCount
                }));
            }
        } catch (error) {
            toast.error('Failed to fetch subscriptions');
        } finally {
            setLoading(false);
        }
    };

    const fetchDatingStatus = async (page = 1) => {
        try {
            setLoading(true);
            const offset = (page - 1) * pagination.limit;
            const response = await fetch(
                `${process.env.REACT_APP_API_URL}/user/date/logs?limit=${pagination.limit}&offset=${offset}&userId=${userId}`,
                {
                    headers: {
                        'Authorization': JSON.parse(sessionStorage.getItem("authUser"))?.token || '',
                    }
                }
            );
            const data = await response.json();
            if (data.message === "Success") {
                setDatingStatus(data.data.data);
                setPagination(prev => ({
                    ...prev,
                    currentPage: page,
                    totalCount: data.data.totalCount
                }));
            }
        } catch (error) {
            toast.error('Failed to fetch dating status');
        } finally {
            setLoading(false);
        }
    };

    const fetchUserProfile = async () => {
        try {
            setLoading(true);
            const response = await fetch(
                `${process.env.REACT_APP_API_URL}/userDetail/admin?userId=${userId}`,
                {
                    headers: {
                        'Authorization': JSON.parse(sessionStorage.getItem("authUser"))?.token || '',
                    }
                }
            );
            const data = await response.json();
            if (data.message === "Success") {
                setUserData(data.data);
            }
        } catch (error) {
            toast.error('Failed to fetch user profile');
        } finally {
            setLoading(false);
        }
    };

    const renderProfileDetails = () => {
        if (!userData) return null;

        return (
            <div className="profile-details">
                <div className="user-header mb-4">
                    <h3>{userData.firstName} {userData.lastName}</h3>
                    <div className="user-meta">
                        <span className="username">@{userData.username}</span>
                        <Badge 
                            color={userData.isVerified ? "success" : "warning"} 
                            className="ms-2"
                        >
                            {userData.isVerified ? "Verified" : "Unverified"}
                        </Badge>
                    </div>
                    <div className="profile-completion-bar mt-3">
                        <div className="d-flex justify-content-between align-items-center mb-2">
                            <span>Profile Completion</span>
                            <span>{userData.profileCompletionPercentage}%</span>
                        </div>
                        <div className="progress" style={{ height: "6px" }}>
                            <div 
                                className="progress-bar" 
                                role="progressbar" 
                                style={{ width: `${userData.profileCompletionPercentage}%` }}
                                aria-valuenow={userData.profileCompletionPercentage} 
                                aria-valuemin="0" 
                                aria-valuemax="100"
                            />
                        </div>
                    </div>
                </div>

                <Row className="stats-cards mb-4">
                    <Col md={4}>
                        <div className="stat-card">
                            <i className="bx bx-heart text-danger"></i>
                            <h4>{userData.likesCount || 0}</h4>
                            <span>Likes</span>
                        </div>
                    </Col>
                    <Col md={4}>
                        <div className="stat-card">
                            <i className="bx bx-user-plus text-primary"></i>
                            <h4>{userData.referralCount || 0}</h4>
                            <span>Referrals</span>
                        </div>
                    </Col>
                    <Col md={4}>
                        <div className="stat-card">
                            <i className="bx bx-coin-stack text-warning"></i>
                            <h4>{userData.tokens || 0}</h4>
                            <span>Tokens</span>
                        </div>
                    </Col>
                </Row>

                <div className="info-sections">
                    <div className="info-card mb-4">
                        <div className="info-header">
                            <i className="bx bx-user-circle"></i>
                            <h5>Basic Information</h5>
                        </div>
                        <div className="info-content">
                            <Row>
                                <Col md={6}>
                                    <div className="info-item">
                                        <label>Email</label>
                                        <p>{userData.email}</p>
                                    </div>
                                    <div className="info-item">
                                        <label>Phone</label>
                                        <p>{userData.phoneNumber}</p>
                                    </div>
                                    <div className="info-item">
                                        <label>Gender</label>
                                        <p>{userData.gender}</p>
                                    </div>
                                </Col>
                                <Col md={6}>
                                    <div className="info-item">
                                        <label>Date of Birth</label>
                                        <p>{moment(userData.dateOfBirth).format('DD MMM YYYY')} ({moment().diff(userData.dateOfBirth, 'years')} years)</p>
                                    </div>
                                    <div className="info-item">
                                        <label>Location</label>
                                        <p>{userData.country}</p>
                                    </div>
                                    <div className="info-item">
                                        <label>Member Since</label>
                                        <p>{moment(userData.creationDate).format('DD MMM YYYY')}</p>
                                    </div>
                                </Col>
                            </Row>
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    useEffect(() => {
        if (activeTab === 'profile') {
            fetchUserProfile();
        } else if (activeTab === 'likes') {
            fetchLikes(1);
        } else if (activeTab === 'referrals') {
            fetchReferrals(1);
        } else if (activeTab === 'subscriptions') {
            fetchSubscriptions(1);
        } else if (activeTab === 'dating') {
            fetchDatingStatus(1);
        }
    }, [activeTab, searchText]);

    useEffect(() => {
        // Initialize all carousels on the page
        const carouselElement = document.getElementById('profileCarousel');
        if (carouselElement) {
            new Carousel(carouselElement, {
                interval: 3000, // Auto slide interval in milliseconds
                wrap: true // Whether the carousel should cycle continuously
            });
        }
    }, [userData]); // Reinitialize when userData changes

    const handlePageChange = (page) => {
        if (activeTab === 'likes') {
            fetchLikes(page);
        } else if (activeTab === 'referrals') {
            fetchReferrals(page);
        } else if (activeTab === 'subscriptions') {
            fetchSubscriptions(page);
        } else if (activeTab === 'dating') {
            fetchDatingStatus(page);
        }
    };

    // Pagination renderer (same as in users.js)
    const renderPaginationItems = () => {
        const totalPages = Math.ceil(pagination.totalCount / pagination.limit);
        const items = [];
        
        for (let i = 1; i <= totalPages; i++) {
            if (
                i === 1 || 
                i === totalPages || 
                (i >= pagination.currentPage - 1 && i <= pagination.currentPage + 1)
            ) {
                items.push(
                    <PaginationItem key={i} active={pagination.currentPage === i}>
                        <PaginationLink onClick={() => handlePageChange(i)}>
                            {i}
                        </PaginationLink>
                    </PaginationItem>
                );
            } else if (i === pagination.currentPage - 2 || i === pagination.currentPage + 2) {
                items.push(
                    <PaginationItem key={i} disabled>
                        <PaginationLink>...</PaginationLink>
                    </PaginationItem>
                );
            }
        }
        return items;
    };

    // Update the getImageUrl function
    const getImageUrl = (url) => {
        try {
            if (!url) return require('../../assets/images/avatar.png');
            if (url.startsWith('http')) return url;
            
            const baseUrl = process.env.REACT_APP_MEDIA_URL.replace(/\/$/, '');
            const imagePath = url.replace(/^\//, '');
            return `${baseUrl}/${imagePath}`;
        } catch (error) {
            return require('../../assets/images/avatar.png');
        }
    };

    // Add this helper function
    const getVideoUrl = (url) => {
        if (!url) return '/images/default-avatar.png';
        if (url.startsWith('http')) return url;
        
        const baseUrl = process.env.REACT_APP_VIDEO_URL.replace(/\/$/, '');
        const imagePath = url.replace(/^\//, '');
        return `${baseUrl}/${imagePath}`;
    };

    // Update the renderProfileCarousel function
    const renderProfileCarousel = () => {
        if (!userData?.profilePic?.length) {
            return (
                <div className="text-center">
                    <img 
                        src={require('../../assets/images/avatar.png')}
                        alt="Default Profile" 
                        className="img-fluid rounded"
                        style={{ maxHeight: '400px' }}
                        onError={(e) => {
                            e.target.onerror = null;
                            e.target.src = require('../../assets/images/avatar.png');
                        }}
                    />
                </div>
            );
        }

        return (
            <div className="profile-carousel">
                <div id="profileCarousel" className="carousel slide" data-bs-ride="carousel" style={{ height: "100%" }}>
                    <div className="carousel-inner">
                        {userData.profilePic.map((pic, index) => (
                            <div key={pic.seqId} className={`carousel-item ${index === 0 ? 'active' : ''}`}>
                                {pic.type === 'VIDEO' ? (
                                    <div className="ratio ratio-16x9 video-wrapper">
                                        <video 
                                            ref={el => videoRefs.current[pic.seqId] = el}
                                            controlsList="nodownload"
                                            poster={pic.thumbnailUrl ? getImageUrl(pic.thumbnailUrl) : ''}
                                            className="w-100"
                                            preload="metadata"
                                            playsInline
                                        >
                                            <source src={getVideoUrl(pic.url)} type="video/mp4" />
                                            <source src={getVideoUrl(pic.url)} type="video/webm" />
                                            <source src={getVideoUrl(pic.url)} type="video/ogg" />
                                            Your browser does not support the video tag.
                                        </video>
                                        <div 
                                            className="video-overlay"
                                            onClick={() => {
                                                const video = videoRefs.current[pic.seqId];
                                                if (video) {
                                                    if (video.paused) {
                                                        video.play();
                                                    } else {
                                                        video.pause();
                                                    }
                                                }
                                            }}
                                        >
                                            <i className="ri-play-circle-line"></i>
                                        </div>
                                    </div>
                                ) : (
                                    <img 
                                        src={getImageUrl(pic.url)} 
                                        alt={`Profile Image ${pic.seqId}`}
                                        className="d-block w-100"
                                    />
                                )}
                            </div>
                        ))}
                    </div>
                    <button className="carousel-control-prev" type="button" data-bs-target="#profileCarousel" data-bs-slide="prev">
                        <span className="carousel-control-prev-icon" aria-hidden="true"></span>
                        <span className="visually-hidden">Previous</span>
                    </button>
                    <button className="carousel-control-next" type="button" data-bs-target="#profileCarousel" data-bs-slide="next">
                        <span className="carousel-control-next-icon" aria-hidden="true"></span>
                        <span className="visually-hidden">Next</span>
                    </button>
                    <div className="carousel-indicators">
                        {userData.profilePic.map((_, index) => (
                            <button
                                key={index}
                                type="button"
                                data-bs-target="#profileCarousel"
                                data-bs-slide-to={index}
                                className={index === 0 ? 'active' : ''}
                                aria-current={index === 0 ? 'true' : 'false'}
                                aria-label={`Slide ${index + 1}`}
                            ></button>
                        ))}
                    </div>
                </div>
            </div>
        );
    };

    const renderPagination = () => {
        const totalPages = Math.ceil(pagination.totalCount / pagination.limit);
        
        if (totalPages <= 0) return null;

        return (
            <Row className="align-items-center mt-2 g-3 text-center text-sm-start">
                <Col sm={4}>
                    <div className="text-muted">
                        Showing <span className="fw-semibold">
                            {Math.min((pagination.currentPage - 1) * pagination.limit + 1, pagination.totalCount)}
                        </span> - <span className="fw-semibold">
                            {Math.min(pagination.currentPage * pagination.limit, pagination.totalCount)}
                        </span> of <span className="fw-semibold">{pagination.totalCount}</span> Results
                    </div>
                </Col>
                <Col sm={8}>
                    <ul className="pagination pagination-separated justify-content-center justify-content-sm-end mb-sm-0">
                        <li className={`page-item ${pagination.currentPage === 1 ? 'disabled' : ''}`}>
                            <button
                                className="page-link"
                                onClick={() => handlePageChange(pagination.currentPage - 1)}
                                disabled={pagination.currentPage === 1}
                            >
                                Previous
                            </button>
                        </li>
                        
                        {renderPaginationItems()}

                        <li className={`page-item ${pagination.currentPage === totalPages ? 'disabled' : ''}`}>
                            <button
                                className="page-link"
                                onClick={() => handlePageChange(pagination.currentPage + 1)}
                                disabled={pagination.currentPage === totalPages}
                            >
                                Next
                            </button>
                        </li>
                    </ul>
                </Col>
            </Row>
        );
    };

    return (
        <div className="page-content">
            <Container fluid>
                <BreadCrumb title="User Details" pageTitle="Users" />
                <Card>
                    <CardBody>
                        <Nav tabs>
                            <NavItem>
                                <NavLink
                                    className={activeTab === 'profile' ? 'active' : ''}
                                    onClick={() => setActiveTab('profile')}
                                >
                                    Profile
                                </NavLink>
                            </NavItem>
                            <NavItem>
                                <NavLink
                                    className={activeTab === 'attributes' ? 'active' : ''}
                                    onClick={() => setActiveTab('attributes')}
                                >
                                    Attributes
                                </NavLink>
                            </NavItem>
                            <NavItem>
                                <NavLink
                                    className={activeTab === 'preferences' ? 'active' : ''}
                                    onClick={() => setActiveTab('preferences')}
                                >
                                    Search Preferences
                                </NavLink>
                            </NavItem>
                            <NavItem>
                                <NavLink
                                    className={activeTab === 'likes' ? 'active' : ''}
                                    onClick={() => setActiveTab('likes')}
                                >
                                    Likes
                                </NavLink>
                            </NavItem>
                            <NavItem>
                                <NavLink
                                    className={activeTab === 'referrals' ? 'active' : ''}
                                    onClick={() => setActiveTab('referrals')}
                                >
                                    Referrals
                                </NavLink>
                            </NavItem>
                            <NavItem>
                                <NavLink
                                    className={activeTab === 'subscriptions' ? 'active' : ''}
                                    onClick={() => setActiveTab('subscriptions')}
                                >
                                    Subscriptions
                                </NavLink>
                            </NavItem>
                            <NavItem>
                                <NavLink
                                    className={activeTab === 'dating' ? 'active' : ''}
                                    onClick={() => setActiveTab('dating')}
                                >
                                    Dating Status
                                </NavLink>
                            </NavItem>
                        </Nav>

                        <TabContent activeTab={activeTab} className="mt-4">
                            <TabPane tabId="profile">
                                <Row>
                                    <Col lg={7} xl={8} className="order-1 order-lg-2 mb-4">
                                        {renderProfileDetails()}
                                    </Col>
                                    <Col lg={5} xl={4} className="order-2 order-lg-1">
                                        <div className="mb-4">
                                            {renderProfileCarousel()}
                                        </div>

                                        <div className="info-card mb-4">
                                            <div className="info-header">
                                                <i className="bx bx-check-shield"></i>
                                                <h5>Account Status</h5>
                                            </div>
                                            <div className="info-content">
                                                <Row>
                                                    <Col xs={12} sm={6}>
                                                        <div className="status-item">
                                                            <i className={`bx ${userData?.isVerified ? 'bxs-check-circle text-success' : 'bxs-x-circle text-danger'}`}></i>
                                                            <span>Profile Verification</span>
                                                        </div>
                                                        <div className="status-item">
                                                            <i className={`bx ${userData?.subscriptionStatus ? 'bxs-check-circle text-success' : 'bxs-x-circle text-danger'}`}></i>
                                                            <span>Premium Subscription</span>
                                                        </div>
                                                    </Col>
                                                    <Col xs={12} sm={6}>
                                                        <div className="status-item">
                                                            <i className="bx bxs-time text-info"></i>
                                                            <span>Last Active: {moment(userData?.lastActiveTs).fromNow()}</span>
                                                        </div>
                                                        <div className="status-item">
                                                            <i className="bx bxs-badge-check text-primary"></i>
                                                            <span>Account Age: {userData?.creationDate ? moment().diff(userData.creationDate, 'days') : 0} days</span>
                                                        </div>
                                                    </Col>
                                                </Row>
                                            </div>
                                        </div>

                                        <div className="info-card mb-4">
                                            <div className="info-header">
                                                <i className="bx bx-message-square-dots"></i>
                                                <h5>About Me</h5>
                                            </div>
                                            <div className="info-content">
                                                <p className="about-text">
                                                    {userData?.aboutMe || 'No description provided.'}
                                                </p>
                                            </div>
                                        </div>
                                    </Col>
                                </Row>
                            </TabPane>

                            <TabPane tabId="attributes">
                                <div className="attributes-wrapper">
                                    {userData?.dynamicAttributes?.length > 0 ? (
                                        userData.dynamicAttributes.map(group => (
                                            <div key={group._id} className="attribute-section mb-4">
                                                <div className="attribute-group-header">
                                                    <i className="bx bx-list-ul"></i>
                                                    <h5>{group.groupTitle}</h5>
                                                </div>
                                                <div className="attribute-group-content">
                                                    <Row>
                                                        {group.attributes
                                                            ?.filter(attr => {
                                                                if (Array.isArray(attr.attributeValue)) {
                                                                    return attr.attributeValue.length > 0;
                                                                }
                                                                return attr.attributeValue !== null && 
                                                                       attr.attributeValue !== undefined && 
                                                                       attr.attributeValue !== "Not Specified" &&
                                                                       attr.attributeValue !== "";
                                                            })
                                                            .map(attr => (
                                                                <Col sm={6} lg={4} key={attr._id} className="mb-3">
                                                                    <div className="attribute-card">
                                                                        <h6 className="attribute-title">
                                                                            {attr.textShownOnProfile}
                                                                        </h6>
                                                                        <p className="attribute-value">
                                                                            {Array.isArray(attr.attributeValue) 
                                                                                ? attr.attributeValue.join(', ')
                                                                                : attr.attributeValue}
                                                                        </p>
                                                                    </div>
                                                                </Col>
                                                            ))}
                                                    </Row>
                                                </div>
                                            </div>
                                        ))
                                    ) : (
                                        <div className="text-center">No attributes found</div>
                                    )}
                                </div>
                            </TabPane>

                            <TabPane tabId="preferences">
                                <div className="preferences-wrapper">
                                    {/* Age & Distance Section */}
                                    <div className="preference-section mb-4">
                                        <div className="preference-group-header">
                                            <i className="bx bx-slider-alt"></i>
                                            <h5>Age & Distance Preferences</h5>
                                        </div>
                                        <div className="preference-group-content">
                                            <Row>
                                                <Col md={6} className="mb-4 mb-md-0">
                                                    <div className="preference-card">
                                                        <div className="preference-icon">
                                                            <i className="bx bx-calendar"></i>
                                                        </div>
                                                        <h6>Age Range</h6>
                                                        <div className="preference-value">
                                                            <span className="range-value">{userData?.searchPreferences?.ageLowerLimit}</span>
                                                            <span className="range-separator">-</span>
                                                            <span className="range-value">{userData?.searchPreferences?.ageHigherLimit}</span>
                                                            <span className="range-unit">years</span>
                                                        </div>
                                                        <p className="preference-note">
                                                            {userData?.searchPreferences?.agerunOut ? 
                                                                'Age limit extended' : 'Strict age limit'}
                                                        </p>
                                                    </div>
                                                </Col>
                                                <Col md={6}>
                                                    <div className="preference-card">
                                                        <div className="preference-icon">
                                                            <i className="bx bx-map"></i>
                                                        </div>
                                                        <h6>Distance Range</h6>
                                                        <div className="preference-value">
                                                            <span className="range-value">{userData?.searchPreferences?.distanceLowerLimit}</span>
                                                            <span className="range-separator">-</span>
                                                            <span className="range-value">{userData?.searchPreferences?.distanceHigherLimit}</span>
                                                            <span className="range-unit">{userData?.searchPreferences?.distanceUnit}</span>
                                                        </div>
                                                        <p className="preference-note">
                                                            {userData?.searchPreferences?.distancerunOut ? 
                                                                'Distance limit extended' : 'Strict distance limit'}
                                                        </p>
                                                    </div>
                                                </Col>
                                            </Row>
                                        </div>
                                    </div>

                                    {/* Match Preferences Section */}
                                    <div className="preference-section mb-4">
                                        <div className="preference-group-header">
                                            <i className="bx bx-heart"></i>
                                            <h5>Match Preferences</h5>
                                        </div>
                                        <div className="preference-group-content">
                                            <Row>
                                                <Col md={6} className="mb-4">
                                                    <div className="preference-card">
                                                        <div className="preference-icon">
                                                            <i className="bx bx-user"></i>
                                                        </div>
                                                        <h6>Interested In</h6>
                                                        <div className="preference-tags">
                                                            {userData?.searchPreferences?.whatGenderOfMatchesAreYouInterestedIn?.length > 0 ? (
                                                                userData.searchPreferences.whatGenderOfMatchesAreYouInterestedIn.map((gender, index) => (
                                                                    <span key={index} className="preference-tag">{gender}</span>
                                                                ))
                                                            ) : (
                                                                <span className="text-muted">Not specified</span>
                                                            )}
                                                        </div>
                                                    </div>
                                                </Col>
                                                <Col md={6} className="mb-4">
                                                    <div className="preference-card">
                                                        <div className="preference-icon">
                                                            <i className="bx bx-show"></i>
                                                        </div>
                                                        <h6>Show Profile To</h6>
                                                        <div className="preference-tags">
                                                            {userData?.searchPreferences?.whoShouldSeeYourProfile?.length > 0 ? (
                                                                userData.searchPreferences.whoShouldSeeYourProfile.map((gender, index) => (
                                                                    <span key={index} className="preference-tag">{gender}</span>
                                                                ))
                                                            ) : (
                                                                <span className="text-muted">Not specified</span>
                                                            )}
                                                        </div>
                                                    </div>
                                                </Col>
                                                <Col md={6}>
                                                    <div className="preference-card">
                                                        <div className="preference-icon">
                                                            <i className="bx bx-check-shield"></i>
                                                        </div>
                                                        <h6>Verification Preference</h6>
                                                        <div className="preference-value">
                                                            {userData?.searchPreferences?.verifiedProfilesOnly ? 
                                                                'Verified Profiles Only' : 'All Profiles'}
                                                        </div>
                                                    </div>
                                                </Col>
                                            </Row>
                                        </div>
                                    </div>

                                    {/* Location Section with Map */}
                                    <div className="preference-section">
                                        <div className="preference-group-header">
                                            <i className="bx bx-map-alt"></i>
                                            <h5>Location Preferences</h5>
                                        </div>
                                        <div className="preference-group-content">
                                            <Row>
                                                <Col md={6}>
                                                    <div className="preference-card">
                                                        <div className="preference-icon">
                                                            <i className="bx bx-current-location"></i>
                                                        </div>
                                                        <h6>Location Details</h6>
                                                        <div className="location-details">
                                                            <p><strong>City:</strong> {userData?.searchPreferences?.location?.city}</p>
                                                            <p><strong>State:</strong> {userData?.searchPreferences?.location?.state}</p>
                                                            <p><strong>Country:</strong> {userData?.searchPreferences?.location?.country}</p>
                                                            <p><strong>Zip Code:</strong> {userData?.searchPreferences?.location?.zipCode}</p>
                                                        </div>
                                                    </div>
                                                </Col>
                                                <Col md={6}>
                                                    <div className="preference-card map-card">
                                                        <iframe
                                                            title="Location Map"
                                                            width="100%"
                                                            height="250"
                                                            frameBorder="0"
                                                            src={`https://maps.google.com/maps?q=${userData?.searchPreferences?.location?.latitude},${userData?.searchPreferences?.location?.longitude}&z=8&output=embed`}
                                                            allowFullScreen
                                                        />
                                                    </div>
                                                </Col>
                                            </Row>
                                        </div>
                                    </div>
                                </div>
                            </TabPane>

                            <TabPane tabId="likes">
                                <Table className="table-hover">
                                    <thead>
                                        <tr>
                                            <th>Username</th>
                                            <th>Name</th>
                                            <th>Age</th>
                                            <th>City</th>
                                            <th>Status</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {likes?.length > 0 ? (
                                            likes.map(like => (
                                                <tr key={like.id}>
                                                    <td>
                                                        <span 
                                                            className="clickable text-primary"
                                                            onClick={() => fetchUserDetails(like.id)}
                                                        >
                                                            {like.username}
                                                        </span>
                                                    </td>
                                                    <td>{`${like.firstName} ${like.lastName}`}</td>
                                                    <td>{like.age}</td>
                                                    <td>{like.city}</td>
                                                    <td>
                                                        <span className={`badge bg-${like.isVerified ? 'success' : 'warning'}`}>
                                                            {like.isVerified ? 'Verified' : 'Not Verified'}
                                                        </span>
                                                    </td>
                                                </tr>
                                            ))
                                        ) : (
                                            <tr>
                                                <td colSpan="5" className="text-center">No records found</td>
                                            </tr>
                                        )}
                                    </tbody>
                                </Table>
                                {likes.length > 0 && renderPagination()}

                                <UserDetailsModal 
                                    isOpen={userDetailsModal}
                                    toggle={() => setUserDetailsModal(!userDetailsModal)}
                                    user={selectedUser}
                                    loading={loadingUser}
                                    getImageUrl={getImageUrl}
                                />
                            </TabPane>

                            <TabPane tabId="referrals">
                                <Table className="table-hover">
                                    <thead>
                                        <tr>
                                            <th>Username</th>
                                            <th>Name</th>
                                            <th>Signup Date</th>
                                            <th>Tokens Earned</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {referrals?.length > 0 ? (
                                            referrals.map(ref => (
                                                <tr key={ref._id}>
                                                    <td>
                                                        <span 
                                                            className="clickable text-primary"
                                                            onClick={() => fetchUserDetails(ref.user._id)}
                                                        >
                                                            {ref.user.username}
                                                        </span>
                                                    </td>
                                                    <td>{`${ref.user.firstName} ${ref.user.lastName}`}</td>
                                                    <td>{moment(ref.user.signupDate).format('DD MMM YYYY')}</td>
                                                    <td>{ref.referredByTokens || 0}</td>
                                                </tr>
                                            ))
                                        ) : (
                                            <tr>
                                                <td colSpan="4" className="text-center">No records found</td>
                                            </tr>
                                        )}
                                    </tbody>
                                </Table>
                                {referrals.length > 0 && renderPagination()}

                                <UserDetailsModal 
                                    isOpen={userDetailsModal}
                                    toggle={() => setUserDetailsModal(!userDetailsModal)}
                                    user={selectedUser}
                                    loading={loadingUser}
                                    getImageUrl={getImageUrl}
                                />
                            </TabPane>

                            <TabPane tabId="subscriptions">
                                <Table className="table-hover">
                                    <thead>
                                        <tr>
                                            <th>Subscription</th>
                                            <th>Status</th>
                                            <th>Start Date</th>
                                            <th>End Date</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {subscriptions?.length > 0 ? (
                                            subscriptions.map(sub => (
                                                <tr key={sub._id}>
                                                    <td>{sub.planName}</td>
                                                    <td>{sub.status}</td>
                                                    <td>{moment(sub.startDate).format('DD MMM YYYY')}</td>
                                                    <td>{moment(sub.endDate).format('DD MMM YYYY')}</td>
                                                </tr>
                                            ))
                                        ) : (
                                            <tr>
                                                <td colSpan="4" className="text-center">No records found</td>
                                            </tr>
                                        )}
                                    </tbody>
                                </Table>
                                {subscriptions.length > 0 && renderPagination()}
                            </TabPane>

                            <TabPane tabId="dating">
                                <Table className="table-hover">
                                    <thead>
                                        <tr>
                                            <th>Date Type</th>
                                            <th>Status</th>
                                            <th>Requested By</th>
                                            <th>Requested To</th>
                                            <th>Location</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {datingStatus?.length > 0 ? (
                                            datingStatus.map(status => (
                                                <tr key={status._id}>
                                                    <td>
                                                        <Badge color="info" className="me-1">
                                                            {status.dateType.replace('_', ' ')}
                                                        </Badge>
                                                    </td>
                                                    <td>{status.status}</td>
                                                    <td>
                                                        <span 
                                                            className="clickable text-primary"
                                                            onClick={() => fetchUserDetails(status.requestedByUser._id)}
                                                        >
                                                            {status.requestedByUser.username}
                                                        </span>
                                                    </td>
                                                    <td>
                                                        <span 
                                                            className="clickable text-primary"
                                                            onClick={() => fetchUserDetails(status.requestedToUser._id)}
                                                        >
                                                            {status.requestedToUser.username}
                                                        </span>
                                                    </td>
                                                    <td>{status.location}</td>
                                                </tr>
                                            ))
                                        ) : (
                                            <tr>
                                                <td colSpan="5" className="text-center">No records found</td>
                                            </tr>
                                        )}
                                    </tbody>
                                </Table>
                                {datingStatus.length > 0 && renderPagination()}

                                <UserDetailsModal 
                                    isOpen={userDetailsModal}
                                    toggle={() => setUserDetailsModal(!userDetailsModal)}
                                    user={selectedUser}
                                    loading={loadingUser}
                                    getImageUrl={getImageUrl}
                                />
                            </TabPane>
                        </TabContent>
                    </CardBody>
                </Card>
            </Container>
        </div>
    );
};

export default UserDetails; 